import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskReveal, Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { Seo } from "../components/Seo";
import { SERVICES, SERVICES_PATH } from "../data/siteData";

const ServiceCard = ({ service, index }) => (
  <Reveal delay={(index % 3) * 0.08} className="h-full">
    <Link
      to={`${SERVICES_PATH}/${service.slug}`}
      data-testid={`service-card-${service.slug}`}
      className="group relative block h-full overflow-hidden rounded-2xl border border-amber-500/15 transition-all duration-500 hover:border-amber-400/40"
    >
      <div className="aspect-[4/3] w-full overflow-hidden">
        <img
          src={service.image}
          alt={service.name}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/35 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-7">
        <div className="min-w-0">
          <p className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-amber-400/80">
            {String(index + 1).padStart(2, "0")} · {service.durations.join(" / ")}
          </p>
          <h3 className="mt-2 font-display text-2xl font-light text-amber-50 sm:text-3xl">{service.name}</h3>
          <p className="mt-2 max-w-md text-sm font-light leading-relaxed text-stone-400">{service.short}</p>
        </div>
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-amber-500/30 text-amber-200 transition-all duration-300 group-hover:bg-amber-400 group-hover:text-[#0B0B0C]">
          <ArrowUpRight className="h-5 w-5" />
        </span>
      </div>
    </Link>
  </Reveal>
);

export default function Services() {
  return (
    <>
      <Seo
        title="Massage Services in Suchitra | Aroma, Balinese, Thai & More"
        description="Explore seven massage services in Suchitra, Hyderabad — aroma therapy, Balinese, deep tissue, Thai, four hand massage, body massage & Jacuzzi spa, 45 to 120 minute sessions."
        keywords="massage services Suchitra, aroma therapy massage Hyderabad, Balinese massage, deep tissue massage, Thai massage Hyderabad, four hand massage, Jacuzzi spa"
      />
      <section className="relative overflow-hidden pb-16 pt-44" data-testid="services-hero">
        <div className="ambient-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <MaskReveal delay={0.15}>
            <p className="font-mono-alt text-[11px] uppercase tracking-[0.35em] text-amber-400/80">Services</p>
          </MaskReveal>
          <h1 className="mt-8 font-display text-4xl font-light leading-[1.06] tracking-tight text-amber-50 sm:text-6xl lg:text-7xl">
            <MaskReveal delay={0.3}>Seven services.</MaskReveal>
            <MaskReveal delay={0.45}>
              <em className="text-gold-gradient not-italic">One intention.</em>
            </MaskReveal>
          </h1>
          <MaskReveal delay={0.65} className="mt-8 max-w-2xl">
            <p className="text-base font-light leading-relaxed text-stone-400 sm:text-lg">
              Every service at Belong is complete in itself — never rushed, never shared. Sessions
              run 45 to 120 minutes; Four Hand and Jacuzzi sessions begin at 60.
            </p>
          </MaskReveal>
        </div>
      </section>

      <section className="pb-24 pt-6" data-testid="services-grid">
        <div className="mx-auto grid max-w-7xl items-stretch gap-6 px-6 sm:grid-cols-2 lg:grid-cols-3 lg:px-10">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.slug} service={s} index={i} />
          ))}
        </div>
        <Reveal className="mx-auto mt-12 max-w-7xl px-6 lg:px-10">
          <p
            className="rounded-2xl border border-amber-500/15 bg-[#121215] px-7 py-5 text-center font-mono-alt text-[10px] uppercase tracking-[0.22em] text-stone-400"
            data-testid="services-duration-note"
          >
            All services priced on request · Four Hand Massage & Jacuzzi Spa begin at 60 minutes
          </p>
        </Reveal>
      </section>

      <CTASection />
    </>
  );
}
